import * as React from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { GroupModel } from '@/app/models/groupModel'
import GroupPicker from '@/modules/drivers/shared/DriverGroupPicker'
import { ConfirmModal } from '@/modules/drivers/modals/ConfirmModal'
import { isAdmin } from '@/app/services/local/roleService'

/**
 * Admin "Test" page for the EasyTMS driver-payroll endpoint
 * POST /api/{companyId}/timesheet/driverPayrollBreakdown (engine-computed breakdown, no proxy hop).
 *
 * The external EasyTMS service knows drivers by akronim (external id / ID Export) and the company by
 * NIP — it has no knowledge of our internal driver ids. So this test calls EXACTLY like EasyTMS would:
 * by akronim. Drivers without an akronim can't be matched and are shown disabled.
 */
type ExtendedDriver = {
  id: number
  fullName: string
  akronim: string | null
  isActive: boolean
}

export const PayrollTestPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const now = new Date()

  const [year, setYear] = React.useState<number>(now.getFullYear())
  const [month, setMonth] = React.useState<number>(now.getMonth() + 1)
  const [nip, setNip] = React.useState<string>('')
  const [tlamToken, setTlamToken] = React.useState<string>('')
  const [drivers, setDrivers] = React.useState<ExtendedDriver[]>([])
  const [driversLoading, setDriversLoading] = React.useState(false)
  const [selected, setSelected] = React.useState<Record<string, boolean>>({})
  const [loading, setLoading] = React.useState(false)
  const [response, setResponse] = React.useState<unknown>(null)
  const [error, setError] = React.useState<string | null>(null)

  // Tabs: EasyTMS driver-payroll test, the settled-drivers report test, the BFM push preview,
  // and the expected-netto (premia/nagroda) pull.
  type Tab = 'payroll' | 'settled' | 'bfm' | 'netto'
  const [tab, setTab] = React.useState<Tab>('payroll')

  // Settled-drivers report (konto serwisowe) filters.
  const [sdNip, setSdNip] = React.useState<string>('')
  const [sdFrom, setSdFrom] = React.useState<string>('')
  const [sdTo, setSdTo] = React.useState<string>('')

  // Expected-netto tab: rows returned by the two BFM pulls, keyed by driver id.
  type NettoRow = {
    driverId: number
    akronim: string | null
    expectedNettoAmount: number | null
    found: boolean
    mandat: number | null
    zaliczka: number | null
    mandatesFound: boolean
  }
  const [nettoRows, setNettoRows] = React.useState<NettoRow[] | null>(null)

  const switchTab = (next: Tab) => {
    setTab(next)
    setResponse(null)
    setError(null)
    setNettoRows(null)
  }

  React.useEffect(() => {
    if (!companyId) return
    setDriversLoading(true)
    axiosInstance
      .get(`/api/${companyId}/drivers/extended`)
      .then((res) =>
        setDrivers(
          (res.data ?? []).map((d: any) => ({
            id: d.id,
            fullName: d.fullName,
            akronim: d.externalId || null,
            isActive: d.isActive !== false,
          })),
        ),
      )
      .catch(() => setDrivers([]))
      .finally(() => setDriversLoading(false))
  }, [companyId])

  // Driver-group filter (same pattern as the drivers tab): groups come from /driver/groups,
  // membership of a selected group from /driver/driversInGroup (groups carry no driver ids).
  const [groups, setGroups] = React.useState<GroupModel[]>([])
  const [actualGroup, setActualGroup] = React.useState<GroupModel | null>(null)
  const [groupDriverIds, setGroupDriverIds] = React.useState<number[]>([])

  const fetchGroups = React.useCallback(() => {
    if (!companyId) return
    axiosInstance
      .get(`/api/${companyId}/driver/groups`)
      .then((res) => setGroups(res.data ?? []))
      .catch(() => setGroups([]))
  }, [companyId])

  React.useEffect(fetchGroups, [fetchGroups])

  React.useEffect(() => {
    if (companyId && actualGroup && actualGroup.id > 0) {
      axiosInstance
        .get(`/api/${companyId}/driver/driversInGroup?groupId=${actualGroup.id}`)
        .then((res) => setGroupDriverIds(res.data ?? []))
        .catch(() => setGroupDriverIds([]))
    } else {
      setGroupDriverIds([])
    }
  }, [actualGroup, companyId])

  // Drivers visible in the picker under the current group filter (mirrors SideDriversTable):
  // 0/null = all, -1 = active, -2 = inactive, >0 = active members of the group.
  const visibleDrivers = React.useMemo(() => {
    if (!actualGroup || actualGroup.id === 0) return drivers
    if (actualGroup.id === -1) return drivers.filter((d) => d.isActive)
    if (actualGroup.id === -2) return drivers.filter((d) => !d.isActive)
    return drivers.filter((d) => groupDriverIds.includes(d.id) && d.isActive)
  }, [drivers, actualGroup, groupDriverIds])

  const selectedAkronims = Object.entries(selected)
    .filter(([, v]) => v)
    .map(([akronim]) => akronim)

  const toggle = (akronim: string) =>
    setSelected((prev) => ({ ...prev, [akronim]: !prev[akronim] }))

  // Selects only the drivers visible under the current group filter.
  const selectAll = () => {
    const next: Record<string, boolean> = {}
    visibleDrivers.forEach((d) => d.akronim && (next[d.akronim] = true))
    setSelected(next)
  }
  const clearAll = () => setSelected({})

  const runRequest = async (fn: () => Promise<{ data: unknown }>) => {
    setLoading(true)
    setError(null)
    setResponse(null)
    try {
      const { data } = await fn()
      setResponse(data)
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Request failed')
    } finally {
      setLoading(false)
    }
  }

  // Engine path (internal): companyId from the route, by akronim.
  const call = () => {
    if (!companyId || selectedAkronims.length === 0) return
    runRequest(() =>
      axiosInstance.post(
        `/api/${companyId}/timesheet/driverPayrollBreakdown`,
        { year, month, akronims: selectedAkronims },
      ),
    )
  }

  // Selected drivers' internal ids (resolved from the akronim-keyed selection).
  const selectedDriverIds = drivers
    .filter((d) => d.akronim && selected[d.akronim])
    .map((d) => d.id)

  // Mandat/zaliczka pull from EasyTMS/BFM (POST .../timesheet/mandatesAdvancesFromEasyTms).
  // companyId-keyed, browser user token (ViewBillingData) — same auth as the engine path.
  const callMandatesAdvances = () => {
    if (!companyId || selectedDriverIds.length === 0) return
    runRequest(() =>
      axiosInstance.post(
        `/api/${companyId}/timesheet/mandatesAdvancesFromEasyTms`,
        { year, month, driverIds: selectedDriverIds },
      ),
    )
  }

  // Expected netto (premia/nagroda) — replays EXACTLY what "Przelicz" in the payroll grid does:
  // POST .../timesheet/expectedNettoFromEasyTms (BFM `expected_net_value`) plus
  // POST .../timesheet/mandatesAdvancesFromEasyTms for the zaliczka, then shows the add-back.
  // BFM's expected_net_value is already after zaliczka, so the value fed into the bonus/reward
  // target is `expected_net_value + zaliczka` (advancesSumPln is signed).
  const callExpectedNetto = () => {
    if (!companyId || selectedDriverIds.length === 0) return
    setNettoRows(null)
    runRequest(async () => {
      const body = { year, month, driverIds: selectedDriverIds }
      const [nettoRes, maRes] = await Promise.all([
        axiosInstance.post(
          `/api/${companyId}/timesheet/expectedNettoFromEasyTms`,
          body,
        ),
        axiosInstance
          .post(`/api/${companyId}/timesheet/mandatesAdvancesFromEasyTms`, body)
          // Best-effort, same as the payroll grid: no mandat/zaliczka data must not hide the netto.
          .catch(() => ({ data: [] as any[] })),
      ])

      const maById: Record<string, any> = {}
      ;((maRes.data as any[]) ?? []).forEach((r) => {
        maById[String(r.driverId)] = r
      })

      const rows: NettoRow[] = ((nettoRes.data as any[]) ?? []).map((r) => {
        const ma = maById[String(r.driverId)]
        return {
          driverId: r.driverId,
          akronim: r.akronim ?? null,
          expectedNettoAmount: r.expectedNettoAmount ?? null,
          found: !!r.found,
          mandat: ma?.found ? ma.mandatesSum ?? 0 : null,
          zaliczka: ma?.found ? ma.advancesSumPln ?? 0 : null,
          mandatesFound: !!ma?.found,
        }
      })
      setNettoRows(rows)

      // Raw responses stay visible in the JSON panel below the table.
      return { data: { expectedNetto: nettoRes.data, mandatesAdvances: maRes.data } }
    })
  }

  // BFM push preview: builds the EXACT payload that would be POSTed to BFM (brutto/netto split)
  // WITHOUT sending it. POST .../timesheet/bfmPayrollPreview {year, month, driverIds}.
  const callBfmPreview = () => {
    if (!companyId || selectedDriverIds.length === 0) return
    runRequest(async () => {
      const res = await axiosInstance.post(
        `/api/${companyId}/timesheet/bfmPayrollPreview`,
        { year, month, driverIds: selectedDriverIds },
      )
      const data: any = res.data
      // Endpoint returns { month, driverCount, json }; `json` is the exact payload string
      // (Newtonsoft, identical to the real push). Parse it so the result panel shows the
      // real payload object instead of an escaped string.
      return { data: data?.json ? JSON.parse(data.json) : data }
    })
  }

  // Real push to BFM — the same send that runs after a calculation, for resending a month by hand.
  // POST .../timesheet/bfmPayrollPush {year, month, driverIds}; global admins only (server-enforced).
  // The confirm modal is open while bfmPush is set: either the picker selection or every driver
  // with a calculated month (GET .../timesheet/bfmCalculatedDrivers).
  const [bfmPush, setBfmPush] = React.useState<{
    driverIds: number[]
    calculated: boolean
  } | null>(null)
  const askBfmPushCalculated = async () => {
    if (!companyId) return
    setLoading(true)
    setError(null)
    try {
      const res = await axiosInstance.get(
        `/api/${companyId}/timesheet/bfmCalculatedDrivers`,
        { params: { year, month } },
      )
      const ids: number[] = res.data ?? []
      if (ids.length === 0) {
        setError(`Brak przeliczonych kierowców za ${year}-${String(month).padStart(2, '0')}`)
        return
      }
      setBfmPush({ driverIds: ids, calculated: true })
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Request failed')
    } finally {
      setLoading(false)
    }
  }
  const callBfmPush = () => {
    const driverIds = bfmPush?.driverIds ?? []
    setBfmPush(null)
    if (!isAdmin() || !companyId || driverIds.length === 0) return
    runRequest(() =>
      axiosInstance.post(`/api/${companyId}/timesheet/bfmPayrollPush`, {
        year,
        month,
        driverIds,
      }),
    )
  }

  // Column map for the BFM accountant export — DriverPayrollAmountsDto snake_case keys → Polish labels
  // (same order as the components list in the UI). `qty` columns are counts (no brutto/netto pair).
  const BFM_EXPORT_COLUMNS: { key: string; label: string; qty?: boolean }[] = [
    { key: 'base_pay', label: 'Zasadnicze' },
    { key: 'absence_compensation', label: 'Wyrównanie za nieobecności' },
    { key: 'leave_equivalent', label: 'Ekwiwalent za urlop' },
    { key: 'bonus', label: 'Premia' },
    { key: 'reward', label: 'Nagroda' },
    { key: 'additional_bonuses', label: 'Dodatkowe premie' },
    { key: 'custom_additions', label: 'Własne dodatki' },
    { key: 'other_components', label: 'Pozostałe składniki' },
    { key: 'on_demand_shifts', label: 'Dyżury' },
    { key: 'eu_work_surcharge', label: 'Wyrównanie/dopłata za pracę w UE' },
    { key: 'sanitary_facility_costs', label: 'Suma sanitariatów' },
    { key: 'laundry_costs', label: 'Suma prania odzieży' },
    { key: 'clothing_costs', label: 'Suma używania odzieży' },
    { key: 'minimum_compensation', label: 'Wyrównanie do minimalnej' },
    { key: 'night_work', label: 'Praca w godzinach nocnych' },
    { key: 'work_beyond_schedule', label: 'Praca ponad plan' },
    { key: 'overtime', label: 'Nadgodziny' },
    { key: 'avg_weekly_overtime', label: 'Godziny nadliczbowe średniotyg.' },
    { key: 'sunday_holiday_supplement', label: 'Dodatki za pracę w niedziele i św.' },
    { key: 'lump_sum_duty', label: 'Ryczałt za dyżury' },
    { key: 'lump_sum_night_work', label: 'Ryczałt za godziny nocne' },
    { key: 'lump_sum_overtime', label: 'Ryczałt za nadgodziny' },
    { key: 'lump_sum_on_demand_night_overtime', label: 'Ryczałty razem (dyżury, nocne, nadliczbowe)' },
    { key: 'lump_sum_settlement', label: 'Rozliczenie ryczałtów' },
    { key: 'trip_costs', label: 'Koszty wyjazdów' },
    { key: 'domestic_allowance_qty', label: 'Diety krajowe (ilość)', qty: true },
    { key: 'domestic_allowance_amount', label: 'Diety krajowe (kwota)' },
    { key: 'foreign_allowance_qty', label: 'Diety zagraniczne (ilość)', qty: true },
    { key: 'foreign_allowance_amount', label: 'Diety zagraniczne (kwota)' },
    { key: 'domestic_accommodation_qty', label: 'Noclegi krajowe (ilość)', qty: true },
    { key: 'domestic_accommodation_amount', label: 'Noclegi krajowe (kwota)' },
    { key: 'foreign_accommodation_qty', label: 'Noclegi zagraniczne (ilość)', qty: true },
    { key: 'foreign_accommodation_amount', label: 'Noclegi zagraniczne (kwota)' },
    { key: 'bills_amount', label: 'Rachunki' },
    { key: 'advance_payments', label: 'Zaliczki' },
    { key: 'zus_relief', label: 'Ulga ZUS' },
    { key: 'gross_pit', label: 'Brutto do PIT' },
    { key: 'pit_relief', label: 'Ulga PIT' },
  ]

  // Excel-friendly CSV: semicolon separator + comma decimals (Polish Excel), UTF-8 BOM added on download.
  // Decimal values are ALWAYS quoted — otherwise an import configured with comma as an extra separator
  // splits "521,90" into two columns and the data drifts away from the headers.
  const csvNum = (v: unknown) =>
    v === null || v === undefined
      ? ''
      : `"${Number(v).toFixed(2).replace('.', ',')}"`
  const csvCell = (v: string) => (/[;"\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v)

  const buildBfmCsv = (payload: any) => {
    const byAkronim: Record<string, string> = {}
    drivers.forEach((d) => {
      if (d.akronim) byAkronim[d.akronim] = d.fullName
    })

    const header = ['Akronim', 'Kierowca', 'Miesiąc rozliczony']
    BFM_EXPORT_COLUMNS.forEach((c) => {
      if (c.qty) header.push(c.label)
      else header.push(`${c.label} (brutto)`, `${c.label} (netto)`)
    })
    header.push(
      'Zaliczka na podatek (miesiąc)',
      'PPK pracownika (miesiąc)',
      'Netto razem (miesiąc)',
      'Pozostałe składniki — szczegóły',
    )

    const rows = (payload?.drivers ?? []).map((d: any) => {
      const row: string[] = [d.akronim ?? '', csvCell(byAkronim[d.akronim] ?? ''), d.monthCalculated ? 'TAK' : 'NIE']
      const brutto = d.amounts?.brutto ?? {}
      const netto = d.amounts?.netto ?? {}
      BFM_EXPORT_COLUMNS.forEach((c) => {
        if (c.qty) row.push(brutto[c.key] ?? '')
        else row.push(csvNum(brutto[c.key]), csvNum(netto[c.key]))
      })
      row.push(csvNum(d.amounts?.tax_advance), csvNum(d.amounts?.employee_ppk), csvNum(d.amounts?.net_total))
      row.push(csvCell(String(brutto.other_components_detail ?? '')))
      return row.join(';')
    })

    return [header.map(csvCell).join(';'), ...rows].join('\r\n')
  }

  // Accountant export: builds the BFM payload for the SELECTED drivers via the preview endpoint
  // (nothing is sent to BFM) and downloads it as an Excel-compatible CSV.
  const exportBfmToExcel = async () => {
    const ids = selectedDriverIds
    if (!companyId || ids.length === 0) return
    setLoading(true)
    setError(null)
    try {
      const res = await axiosInstance.post(
        `/api/${companyId}/timesheet/bfmPayrollPreview`,
        { year, month, driverIds: ids },
      )
      const payload = JSON.parse((res.data as any)?.json ?? '{}')
      const csv = buildBfmCsv(payload)
      const monthStr = `${year}-${String(month).padStart(2, '0')}`
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `bfm_payroll_${monthStr}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Export failed')
    } finally {
      setLoading(false)
    }
  }

  // External path (as EasyTMS uses it): by company NIP, via the integration endpoint.
  // The endpoint is [ServiceAccountClient]-guarded → needs a service-account (tlam) token, not the
  // browser user token. Paste the tlam access token to override the Authorization header for this call.
  const callByNip = () => {
    if (!nip.trim() || selectedAkronims.length === 0) return
    const monthStr = `${year}-${String(month).padStart(2, '0')}`
    const url = `/config-api/integration/company/driver-payroll`
    const body = { nip: nip.trim(), akronims: selectedAkronims, month: monthStr }
    runRequest(() =>
      tlamToken.trim()
        ? // raw axios → bypasses the interceptor that injects the browser user token
          axios.post(url, body, {
            headers: { Authorization: `Bearer ${tlamToken.trim()}` },
          })
        : axiosInstance.post(url, body),
    )
  }

  // Settled-drivers report (as EasyTMS' settled endpoint, but via the admin-gated test twin).
  // GET /config-api/integration/company/settled-drivers-test?nip&from&to — authorized on the
  // admin browser session (no tlam token needed). from/to filter the settlement period (MonthFirstDay).
  const callSettled = () => {
    if (!sdNip.trim()) return
    runRequest(() =>
      axiosInstance.get(
        `/config-api/integration/company/settled-drivers-test`,
        {
          params: {
            nip: sdNip.trim(),
            from: sdFrom || undefined,
            to: sdTo || undefined,
          },
        },
      ),
    )
  }

  const tabClass = (active: boolean) =>
    `tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-border-b-2 ${
      active
        ? 'tw-border-blue-600 tw-text-blue-600'
        : 'tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700'
    }`

  // Shared akronim-keyed driver picker (reused by the payroll and BFM-preview tabs),
  // filterable by driver group like the drivers tab.
  const driverPicker = (
    <div className="tw-border tw-rounded tw-p-3">
      <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2 tw-flex-wrap">
        <span className="tw-font-medium tw-text-sm">
          Kierowcy z akronimem (
          {visibleDrivers.filter((d) => d.akronim).length}/{visibleDrivers.length})
        </span>
        <GroupPicker
          options={groups}
          companyId={companyId ?? ''}
          refreshGroups={fetchGroups}
          onSelect={setActualGroup}
          actualGroup={actualGroup}
        />
        <button onClick={selectAll} className="tw-text-xs tw-text-primary">
          Zaznacz wszystkich{actualGroup && actualGroup.id !== 0 ? ' (z filtra)' : ''}
        </button>
        <button onClick={clearAll} className="tw-text-xs tw-text-gray-500">
          Wyczyść
        </button>
      </div>
      {driversLoading ? (
        <div className="tw-text-sm tw-text-gray-500">Ładowanie…</div>
      ) : (
        <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-1 tw-max-h-64 tw-overflow-auto">
          {visibleDrivers.map((d) => (
            <label
              key={d.id}
              className={`tw-flex tw-items-center tw-gap-2 tw-text-sm ${d.akronim ? '' : 'tw-opacity-50'}`}
              title={d.akronim ? d.akronim : 'Brak akronimu — EasyTMS nie dopasuje'}
            >
              <input
                type="checkbox"
                disabled={!d.akronim}
                checked={!!(d.akronim && selected[d.akronim])}
                onChange={() => d.akronim && toggle(d.akronim)}
              />
              <span>
                {d.fullName}{' '}
                <span className="tw-text-gray-400">
                  {d.akronim ? d.akronim : '(brak akronimu)'}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <div className="tw-p-6 tw-flex tw-flex-col tw-gap-4 tw-max-w-[1100px]">
      <div>
        <h1 className="tw-text-xl tw-font-semibold">Test — integracja EasyTMS</h1>
      </div>

      {/* Tabs */}
      <div className="tw-flex tw-gap-1 tw-border-b">
        <button
          className={tabClass(tab === 'payroll')}
          onClick={() => switchTab('payroll')}
        >
          Driver payroll (akronimy)
        </button>
        <button
          className={tabClass(tab === 'settled')}
          onClick={() => switchTab('settled')}
        >
          Rozliczeni kierowcy (konto serwisowe)
        </button>
        <button
          className={tabClass(tab === 'bfm')}
          onClick={() => switchTab('bfm')}
        >
          Podgląd wysyłki BFM
        </button>
        <button
          className={tabClass(tab === 'netto')}
          onClick={() => switchTab('netto')}
        >
          Oczekiwane netto (premia/nagroda)
        </button>
      </div>

      {tab === 'payroll' && (
        <>
          <p className="tw-text-sm tw-text-gray-500">
            POST /api/{companyId}/timesheet/driverPayrollBreakdown — dopasowanie po
            akronimie (jak EasyTMS). Przycisk „Mandaty + zaliczki" woła POST
            /api/{companyId}/timesheet/mandatesAdvancesFromEasyTms (pull
            mandat/zaliczka z BFM).
          </p>

      {/* Controls */}
      <div className="tw-flex tw-items-end tw-gap-3 tw-flex-wrap">
        <label className="tw-flex tw-flex-col tw-text-sm">
          Rok
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="tw-border tw-rounded tw-px-2 tw-py-1 tw-w-28"
          />
        </label>
        <label className="tw-flex tw-flex-col tw-text-sm">
          Miesiąc
          <input
            type="number"
            min={1}
            max={12}
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="tw-border tw-rounded tw-px-2 tw-py-1 tw-w-28"
          />
        </label>
        <button
          onClick={call}
          disabled={loading || selectedAkronims.length === 0}
          className="tw-bg-primary tw-text-white tw-rounded tw-px-4 tw-py-2 tw-text-sm tw-font-medium disabled:tw-opacity-50"
          title="Ścieżka wewnętrzna: companyId z URL, silnik (pełne rozbicie)"
        >
          {loading ? 'Wywołuję…' : `Wywołaj — silnik (${selectedAkronims.length})`}
        </button>
        <button
          onClick={callMandatesAdvances}
          disabled={loading || selectedDriverIds.length === 0}
          className="tw-bg-amber-600 tw-text-white tw-rounded tw-px-4 tw-py-2 tw-text-sm tw-font-medium disabled:tw-opacity-50"
          title="Pull mandatów i zaliczek z EasyTMS/BFM: POST /api/{companyId}/timesheet/mandatesAdvancesFromEasyTms {year, month, driverIds}"
        >
          {loading
            ? 'Wywołuję…'
            : `Mandaty + zaliczki (BFM) — ${selectedDriverIds.length}`}
        </button>
      </div>

      {/* External (EasyTMS) path: by company NIP */}
      <div className="tw-flex tw-items-end tw-gap-3 tw-flex-wrap tw-border-t tw-pt-3">
        <label className="tw-flex tw-flex-col tw-text-sm">
          NIP firmy (jak EasyTMS)
          <input
            type="text"
            value={nip}
            placeholder="np. 1234567890"
            onChange={(e) => setNip(e.target.value)}
            className="tw-border tw-rounded tw-px-2 tw-py-1 tw-w-48"
          />
        </label>
        <label className="tw-flex tw-flex-col tw-text-sm tw-flex-1 tw-min-w-[280px]">
          Token konta serwisowego (tlam) — Bearer
          <input
            type="password"
            value={tlamToken}
            placeholder="wklej access_token z client_credentials (tlam)"
            onChange={(e) => setTlamToken(e.target.value)}
            className="tw-border tw-rounded tw-px-2 tw-py-1 tw-w-full"
            autoComplete="off"
          />
        </label>
        <button
          onClick={callByNip}
          disabled={loading || selectedAkronims.length === 0 || !nip.trim()}
          className="tw-bg-gray-700 tw-text-white tw-rounded tw-px-4 tw-py-2 tw-text-sm tw-font-medium disabled:tw-opacity-50"
          title="Ścieżka zewnętrzna jak EasyTMS: POST /config-api/integration/company/driver-payroll {nip, akronims, month}"
        >
          {loading
            ? 'Wywołuję…'
            : `Wywołaj EasyTMS — NIP firmy + akronimy kierowców (${selectedAkronims.length})`}
        </button>
        <span className="tw-text-xs tw-text-gray-400 tw-self-center">
          POST /config-api/integration/company/driver-payroll — bez tokenu tlam
          dostaniesz 403 (guard service-account)
        </span>
      </div>

      {/* Driver picker */}
      {driverPicker}
        </>
      )}

      {tab === 'settled' && (
        <>
          <p className="tw-text-sm tw-text-gray-500">
            GET /config-api/integration/company/settled-drivers-test?nip&from&to —
            raport rozliczonych kierowców. Dla konta serwisowego (IsServiceCompany)
            zwraca mapę firm (metadane + kierowcy) per NIP; dla zwykłej firmy — płaską
            listę. Filtry Od/Do dotyczą DATY ROZLICZENIA (CalculationDate — kiedy
            rozliczono), a nie miesiąca którego dotyczy — więc np. rozliczenie stycznia
            zrobione w czerwcu trafi w zakres czerwca. „Do" jest włącznie (cały dzień).
            Endpoint testowy jest chroniony rolą admina — token tlam nie jest potrzebny.
          </p>

          <div className="tw-flex tw-items-end tw-gap-3 tw-flex-wrap">
            <label className="tw-flex tw-flex-col tw-text-sm">
              NIP firmy / konta serwisowego
              <input
                type="text"
                value={sdNip}
                placeholder="np. 5242885341"
                onChange={(e) => setSdNip(e.target.value)}
                className="tw-border tw-rounded tw-px-2 tw-py-1 tw-w-48"
              />
            </label>
            <label className="tw-flex tw-flex-col tw-text-sm">
              Od (data rozliczenia ≥)
              <input
                type="date"
                value={sdFrom}
                onChange={(e) => setSdFrom(e.target.value)}
                className="tw-border tw-rounded tw-px-2 tw-py-1 tw-w-44"
              />
            </label>
            <label className="tw-flex tw-flex-col tw-text-sm">
              Do (data rozliczenia ≤, włącznie)
              <input
                type="date"
                value={sdTo}
                onChange={(e) => setSdTo(e.target.value)}
                className="tw-border tw-rounded tw-px-2 tw-py-1 tw-w-44"
              />
            </label>
            <button
              onClick={callSettled}
              disabled={loading || !sdNip.trim()}
              className="tw-bg-primary tw-text-white tw-rounded tw-px-4 tw-py-2 tw-text-sm tw-font-medium disabled:tw-opacity-50"
              title="GET /config-api/integration/company/settled-drivers-test {nip, from?, to?}"
            >
              {loading ? 'Wywołuję…' : 'Testuj — rozliczeni kierowcy'}
            </button>
            <button
              onClick={() => {
                setSdFrom('')
                setSdTo('')
              }}
              disabled={loading}
              className="tw-text-xs tw-text-gray-500 tw-self-center"
            >
              Wyczyść daty
            </button>
          </div>
        </>
      )}

      {tab === 'bfm' && (
        <>
          <p className="tw-text-sm tw-text-gray-500">
            Podgląd dokładnego payloadu, który zostałby wysłany do BFM po
            przeliczeniu (POST /api/{companyId}/timesheet/bfmPayrollPreview). Kwoty
            są rozbite na <span className="tw-font-medium">brutto</span> i{' '}
            <span className="tw-font-medium">netto</span>. Przycisk „Sprawdź"{' '}
            <span className="tw-font-medium">nic nie wysyła do BFM</span> — buduje
            payload identycznie jak realna wysyłka i pokazuje go poniżej. „Wyślij
            przeliczonych" robi <span className="tw-font-medium">realną wysyłkę</span>{' '}
            wszystkich kierowców z przeliczoną ewidencją za wybrany miesiąc (bez
            zaznaczania), „Wyślij zaznaczonych" — tylko zaznaczonych. Obie pytają o
            potwierdzenie i pokazują odpowiedź BFM. Wysyłane
            są tylko kierowcy z akronimem.
          </p>

          <div className="tw-flex tw-items-end tw-gap-3 tw-flex-wrap">
            <label className="tw-flex tw-flex-col tw-text-sm">
              Rok
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="tw-border tw-rounded tw-px-2 tw-py-1 tw-w-28"
              />
            </label>
            <label className="tw-flex tw-flex-col tw-text-sm">
              Miesiąc
              <input
                type="number"
                min={1}
                max={12}
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                className="tw-border tw-rounded tw-px-2 tw-py-1 tw-w-28"
              />
            </label>
            <button
              onClick={callBfmPreview}
              disabled={loading || selectedDriverIds.length === 0}
              className="tw-bg-primary tw-text-white tw-rounded tw-px-4 tw-py-2 tw-text-sm tw-font-medium disabled:tw-opacity-50"
              title="Podgląd payloadu BFM (bez wysyłki): POST /api/{companyId}/timesheet/bfmPayrollPreview {year, month, driverIds}"
            >
              {loading ? 'Sprawdzam…' : `Sprawdź (${selectedDriverIds.length})`}
            </button>
            <button
              onClick={exportBfmToExcel}
              disabled={loading || selectedDriverIds.length === 0}
              className="tw-bg-green-700 tw-text-white tw-rounded tw-px-4 tw-py-2 tw-text-sm tw-font-medium disabled:tw-opacity-50"
              title="Pobiera CSV (otwiera się w Excelu) z kwotami brutto/netto ZAZNACZONYCH kierowców za wybrany miesiąc — nic nie wysyła do BFM. Przy wielu kierowcach może potrwać kilka minut."
            >
              {loading
                ? 'Generuję…'
                : `Eksport do Excela — zaznaczeni (${selectedDriverIds.length})`}
            </button>
            {isAdmin() && (
              <>
                <button
                  onClick={askBfmPushCalculated}
                  disabled={loading}
                  className="tw-bg-red-700 tw-text-white tw-rounded tw-px-4 tw-py-2 tw-text-sm tw-font-medium disabled:tw-opacity-50"
                  title="Realna wysyłka do BFM wszystkich kierowców z przeliczoną ewidencją za wybrany miesiąc — bez zaznaczania"
                >
                  {loading ? 'Wysyłam…' : 'Wyślij przeliczonych do BFM'}
                </button>
                <button
                  onClick={() =>
                    setBfmPush({ driverIds: selectedDriverIds, calculated: false })
                  }
                  disabled={loading || selectedDriverIds.length === 0}
                  className="tw-bg-red-700 tw-text-white tw-rounded tw-px-4 tw-py-2 tw-text-sm tw-font-medium disabled:tw-opacity-50"
                  title="Realna wysyłka do BFM, jak po przeliczeniu: POST /api/{companyId}/timesheet/bfmPayrollPush {year, month, driverIds}"
                >
                  {loading
                    ? 'Wysyłam…'
                    : `Wyślij zaznaczonych do BFM (${selectedDriverIds.length})`}
                </button>
              </>
            )}
            <span className="tw-text-xs tw-text-gray-400 tw-self-center">
              eksport liczy zaznaczonych kierowców — może chwilę potrwać
            </span>
          </div>

          {driverPicker}

          <ConfirmModal
            isOpen={bfmPush != null}
            onClose={() => setBfmPush(null)}
            onConfirm={callBfmPush}
            title="Wysłać listę płac do BFM?"
            text={`Miesiąc ${year}-${String(month).padStart(2, '0')}, ${
              bfmPush?.calculated ? 'kierowcy z przeliczoną ewidencją' : 'zaznaczeni kierowcy'
            }: ${bfmPush?.driverIds.length ?? 0} (wysłani zostaną tylko ci z akronimem). BFM nadpisze dane tych kierowców za ten miesiąc.`}
            actionText="Wyślij"
          />
        </>
      )}

      {tab === 'netto' && (
        <>
          <p className="tw-text-sm tw-text-gray-500">
            Pokazuje <span className="tw-font-medium">expected_net_value</span> z
            BFM — wartość, na której opiera się wyliczenie premii i nagrody (POST
            /api/{companyId}/timesheet/expectedNettoFromEasyTms). Równolegle
            wołane jest{' '}
            <span className="tw-font-medium">mandatesAdvancesFromEasyTms</span>,
            bo BFM zwraca netto <span className="tw-font-medium">już po
            potrąceniu zaliczki</span> — „Przelicz" w rozliczeniach dodaje
            zaliczkę z powrotem, więc celem premii/nagrody jest{' '}
            <span className="tw-font-medium">expected_net_value + zaliczka</span>{' '}
            (zaliczka jest ze znakiem: + potrącenie, − zwrot). Nic nie jest
            zapisywane — to tylko podgląd tego, co dostajemy z BFM.
          </p>

          <div className="tw-flex tw-items-end tw-gap-3 tw-flex-wrap">
            <label className="tw-flex tw-flex-col tw-text-sm">
              Rok
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="tw-border tw-rounded tw-px-2 tw-py-1 tw-w-28"
              />
            </label>
            <label className="tw-flex tw-flex-col tw-text-sm">
              Miesiąc
              <input
                type="number"
                min={1}
                max={12}
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                className="tw-border tw-rounded tw-px-2 tw-py-1 tw-w-28"
              />
            </label>
            <button
              onClick={callExpectedNetto}
              disabled={loading || selectedDriverIds.length === 0}
              className="tw-bg-primary tw-text-white tw-rounded tw-px-4 tw-py-2 tw-text-sm tw-font-medium disabled:tw-opacity-50"
              title="POST /api/{companyId}/timesheet/expectedNettoFromEasyTms {year, month, driverIds} + mandatesAdvancesFromEasyTms"
            >
              {loading
                ? 'Pobieram…'
                : `Pobierz oczekiwane netto z BFM (${selectedDriverIds.length})`}
            </button>
          </div>

          {nettoRows && (
            <div className="tw-overflow-auto tw-border tw-rounded">
              <table className="tw-w-full tw-text-sm">
                <thead className="tw-bg-gray-100">
                  <tr>
                    <th className="tw-text-left tw-px-2 tw-py-1">Kierowca</th>
                    <th className="tw-text-left tw-px-2 tw-py-1">Akronim</th>
                    <th className="tw-text-right tw-px-2 tw-py-1">
                      expected_net_value (BFM)
                    </th>
                    <th className="tw-text-right tw-px-2 tw-py-1">
                      Zaliczka (BFM)
                    </th>
                    <th className="tw-text-right tw-px-2 tw-py-1">
                      Mandat (BFM)
                    </th>
                    <th className="tw-text-right tw-px-2 tw-py-1">
                      Netto do premii/nagrody (netto + zaliczka)
                    </th>
                    <th className="tw-text-left tw-px-2 tw-py-1">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {nettoRows.map((r) => {
                    const driver = drivers.find((d) => d.id === r.driverId)
                    const target =
                      r.expectedNettoAmount != null
                        ? r.expectedNettoAmount + (r.zaliczka ?? 0)
                        : null
                    const num = (v: number | null) =>
                      v == null ? '—' : v.toFixed(2)
                    return (
                      <tr key={r.driverId} className="tw-border-t">
                        <td className="tw-px-2 tw-py-1">
                          {driver?.fullName ?? r.driverId}
                        </td>
                        <td className="tw-px-2 tw-py-1 tw-text-gray-500">
                          {r.akronim ?? '(brak)'}
                        </td>
                        <td className="tw-px-2 tw-py-1 tw-text-right">
                          {num(r.expectedNettoAmount)}
                        </td>
                        <td className="tw-px-2 tw-py-1 tw-text-right">
                          {num(r.zaliczka)}
                        </td>
                        <td className="tw-px-2 tw-py-1 tw-text-right">
                          {num(r.mandat)}
                        </td>
                        <td className="tw-px-2 tw-py-1 tw-text-right tw-font-medium">
                          {num(target)}
                        </td>
                        <td className="tw-px-2 tw-py-1">
                          {r.found ? (
                            <span className="tw-text-green-700">
                              netto OK
                              {!r.mandatesFound && (
                                <span className="tw-text-gray-400">
                                  {' '}
                                  / brak mandat+zaliczka
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="tw-text-red-600">
                              {r.akronim
                                ? 'BFM nie zwrócił netto'
                                : 'brak akronimu'}
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {driverPicker}
        </>
      )}

      {/* Result */}
      {error && <div className="tw-text-red-600 tw-text-sm">Błąd: {error}</div>}
      {response != null && (
        <pre className="tw-bg-gray-900 tw-text-green-200 tw-text-xs tw-rounded tw-p-3 tw-overflow-auto tw-max-h-[600px]">
          {JSON.stringify(response, null, 2)}
        </pre>
      )}
    </div>
  )
}

export default PayrollTestPage
